import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { InputField } from "src/shared_components/FormElements";
import ChipInputField from "src/shared_components/FormElements/ChipInputField";
import EmailEditor from "./EmailEditor";
import CustomLoader from "src/shared_components/CustomLoader";
import { useSnackbar } from "src/context/ShowMessage";
import { useTranslation } from "react-i18next";
import { sendEmail } from "src/apis/ContractApis";

interface Doc {
  clientId: number;
  fieldId: number;
  objectTypeId: number;
  fileId: number;
  directory: string;
  fileName: string;
  isDefault: boolean;
  viewName: string;
  contentType: string;
  fileByte: string;
  url: string | null;
  isImage: boolean;
  file: any | null;
  type: string;
}

interface DocType {
  label: string;
  type: string;
  fetch?: (id: number) => Promise<any>;
  previewFetch?: (doc: Doc) => Promise<any>;
}

interface EmailDialogProps {
  open: boolean;
  onClose: any;
  fetchData?: any;
  sendFunction?: any;
  title: string;
  id?: number;
  docTypes?: any;
}

const schema = yup.object().shape({
  fromName: yup.string().required("From Name is required"),
  subject: yup.string().required("Subject is required"),
  to: yup
    .array()
    .of(yup.string().email("Invalid email format"))
    .min(1, "At least one recipient is required")
    .required("To field is required"),
  cc: yup.array().of(yup.string().email("Invalid email format")).nullable(),
});

const EmailDialog: React.FC<EmailDialogProps> = ({
  open,
  onClose,
  fetchData,
  title,
  sendFunction,
  id = 0,
  docTypes,
}) => {
  const { t } = useTranslation();
  const { displayMessage } = useSnackbar();
  const [template, setTemplate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [availableDocs, setAvailableDocs] = useState<Doc[]>([]);
  const [selectedInsuranceDocs, setSelectedInsuranceDocs] = useState<string[]>(
    []
  );

  const methods = useForm<any>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      fromName: "",
      subject: "",
      to: [],
      cc: [],
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open && fetchData) {
      const fetchEmailTemplate = async () => {
        setLoading(true);
        try {
          const response = await fetchData();
          if (response?.status === 200 && response?.data) {
            reset(response.data);
            setTemplate(response.data.emailBody);
          } else {
            displayMessage(
              response?.data || "Failed to fetch email content",
              "error"
            );
          }
        } catch (error: any) {
          displayMessage(error.message, "error");
        } finally {
          setLoading(false);
        }
      };
      fetchEmailTemplate();
    }
  }, [open, fetchData, reset, displayMessage]);

  const handleDocCheckboxChange = async (type: string, checked: boolean) => {
    setSelectedDocTypes((prev) =>
      checked ? [...prev, type] : prev.filter((d) => d !== type)
    );

    if (checked) {
      const docFetch = docTypes?.find((d: any): any => d.type === type)?.fetch;
      if (docFetch) {
        setDocLoading(true);
        try {
          const res = await docFetch(id);
          if (res?.status === 200 && res?.data) {
            const newDocs = res.data.map((doc: Doc) => ({
              ...doc,
              type,
            }));
            setAvailableDocs((prev) => [
              ...prev.filter((d) => d.type !== type),
              ...newDocs,
            ]);
            // if (type === "VehicleInsurance") {
            //   setSelectedInsuranceDocs(
            //     newDocs.map((doc: Doc) => doc.directory)
            //   );
            // }
          } else {
            displayMessage(
              res?.data || `Failed to fetch ${type} documents`,
              "error"
            );
          }
        } catch (error: any) {
          displayMessage(error.message, "error");
        } finally {
          setDocLoading(false);
        }
      } else {
        setAvailableDocs((prev) => [
          ...prev.filter((d) => d.type !== type),
          {
            clientId: 0,
            fieldId: id,
            objectTypeId: 0,
            fileId: 0,
            directory: type,
            fileName: type,
            isDefault: false,
            viewName: type,
            contentType: "",
            fileByte: "",
            url: null,
            isImage: false,
            file: null,
            type,
          },
        ]);
      }
    } else {
      setAvailableDocs((prev) => prev.filter((d) => d.type !== type));
      if (type === "VehicleInsurance") {
        setSelectedInsuranceDocs([]);
      }
    }
  };

  const handleInsuranceDocChange = (docId: string, checked: boolean) => {
    setSelectedInsuranceDocs((prev) =>
      checked ? [...prev, docId] : prev.filter((id: any) => id !== docId)
    );
  };

  const handlePreview = async (doc: Doc) => {
    const docType = docTypes?.find((d: any) => d.type === doc.type);
    if (docType?.previewFetch) {
      try {
        await docType.previewFetch(doc);
      } catch (error: any) {
        displayMessage(error.message, "error");
      }
    }
  };

  const onSubmit = async (values: any) => {
    setLoading(true);
    const details = [
      ...selectedDocTypes
        .filter((type) => type !== "VehicleInsurance")
        .map((type) => ({
          attachmentType: type,
          key: null,
        })),

      ...selectedInsuranceDocs.map((docId) => ({
        attachmentType: "VehicleInsurance",
        key: docId,
      })),
    ];

    try {
      const body = {
        ...values,
        emailBody: template,
        fieldId: id,
        attachmentsDetails: details,
      };
      const response = sendFunction
        ? await sendFunction(id, body)
        : await sendEmail(id, body);

      if (response?.status === 200) {
        displayMessage("Email sent successfully", "success");
        onClose();
      } else {
        displayMessage(response?.data || "Failed to send email", "error");
      }
    } catch (error: any) {
      displayMessage(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const emailSchema = yup
    .string()
    .email("Invalid email format")
    .required("Email is required");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ style: { width: "80%", borderRadius: 8 } }}
    >
      <DialogTitle sx={{ fontWeight: "bold", bgcolor: "#f5f5f5", py: 2 }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <CustomLoader />
        ) : (
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={6}>
              <InputField
                fieldName="fromName"
                control={control}
                label="From *"
                error={!!errors?.fromName}
                helperText={errors?.fromName?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputField
                fieldName="subject"
                control={control}
                label="Subject *"
                error={!!errors?.subject}
                helperText={errors?.subject?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInputField
                fieldName="to"
                control={control}
                label="To *"
                error={!!errors?.to}
                helperText={errors?.to?.message}
                setValue={setValue}
                schema={emailSchema}
                setError={setError}
                clearError={clearErrors}
                placeholder="Enter TO emails ..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChipInputField
                fieldName="cc"
                control={control}
                label="CC"
                error={!!errors?.cc}
                helperText={errors?.cc?.message}
                setValue={setValue}
                schema={emailSchema}
                setError={setError}
                clearError={clearErrors}
                placeholder="Enter CC emails ..."
              />
            </Grid>
            {docTypes?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Select Documents to Attach
                </Typography>
                <Grid container spacing={2}>
                  {docTypes?.map((doc: any) => (
                    <Grid item key={doc.type}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedDocTypes.includes(doc.type)}
                            onChange={(e) =>
                              handleDocCheckboxChange(
                                doc.type,
                                e.target.checked
                              )
                            }
                            color="primary"
                          />
                        }
                        label={doc.label}
                        sx={{ mr: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {docLoading && (
                  <CircularProgress size={20} sx={{ mt: 1, ml: 2 }} />
                )}
              </Grid>
            )}
            {availableDocs.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Available Documents
                </Typography>
                <List sx={{ bgcolor: "#fafafa", borderRadius: 2, p: 2 }}>
                  {availableDocs.map((doc) => (
                    <React.Fragment key={doc.fileId}>
                      <ListItem>
                        {doc.viewName === "Insurance Policy" ? (
                          doc.contentType === "application/pdf" && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedInsuranceDocs.includes(
                                    doc.directory
                                  )}
                                  onChange={(e) =>
                                    handleInsuranceDocChange(
                                      doc.directory,
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              }
                              label={
                                <ListItemText
                                  primary={doc.fileName}
                                  secondary={`Type: ${doc.viewName}`}
                                />
                              }
                            />
                          )
                        ) : (
                          <ListItemText
                            primary={doc.fileName}
                            secondary={`Type: ${doc.viewName}`}
                          />
                        )}
                        <ListItemSecondaryAction>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => handlePreview(doc)}
                            disabled={
                              !docTypes?.find((d: any) => d.type === doc.type)
                                ?.previewFetch
                            }
                          >
                            Preview
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Grid>
            )}

            {availableDocs.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Documents attached in email:</strong>{" "}
                  {availableDocs
                    .filter(
                      (doc) =>
                        doc.viewName !== "Insurance Policy" ||
                        selectedInsuranceDocs.includes(doc.directory)
                    )
                    .map((doc) => doc.fileName)
                    .join(", ")}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <EmailEditor template={template} setTemplate={setTemplate} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: "#f5f5f5" }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
          sx={{ mr: 1 }}
        >
          {t("common.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {t("common.buttons.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDialog;
